import { Copy, CopyCheck, Pause, Play } from "lucide-react";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { toast } from "sonner";
import { useFontStyle } from "~/hook/useFontStyle";
import { DohaChopaiDataType } from "~/interface/doha-chopai/doha-chopai";
import { cn } from "~/lib/utils";
import { MstPersonType } from "~/type";

interface ListViewProps {
  data: DohaChopaiDataType[];
  category?: MstPersonType[] | null;
  chopaiSource?: MstPersonType[] | null;
}

const ListView = forwardRef<HTMLDivElement, ListViewProps>(({ data, category, chopaiSource }, ref) => {
  const [playingAudioId, setPlayingAudioId] = useState<number | null>(null);
  const [copyText, setCopyText] = useState<Record<number, boolean>>({});
  const { normalInter, regular } = useFontStyle();
  const observerRef = useRef<HTMLDivElement | null>(null);

  useImperativeHandle(ref, () => observerRef.current as HTMLDivElement);

  const handleCopy = (id: number, text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopyText((prev) => ({ ...prev, [id]: true }));
        toast.custom((t) => (
          <div className="w-full bg-white border border-borderPrimary rounded-lg shadow-lg px-4 py-3 cursor-pointer" onClick={() => toast.dismiss(t)}>
            <div className="flex items-start gap-3">
              <CopyCheck className="w-5 h-5 text-textHeadingColor mt-0.5" />

              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-textHeadingColor">Copied to clipboard</span>

                <span className={cn("text-xs text-textColorPrimary line-clamp-2 whitespace-pre-wrap capitalize", regular)}>{text}</span>
              </div>
            </div>
          </div>
        ));
        setTimeout(() => {
          setCopyText((prev) => ({ ...prev, [id]: false }));
        }, 2000);
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };

  const handlePlayPause = (id: number, audioElement: HTMLAudioElement) => {
    if (playingAudioId === id) {
      audioElement.pause();
      setPlayingAudioId(null);
    } else {
      if (playingAudioId !== null) {
        const currentlyPlayingAudio = document.getElementById(`audio-${playingAudioId}`) as HTMLAudioElement;
        if (currentlyPlayingAudio) {
          currentlyPlayingAudio.pause();
          currentlyPlayingAudio.currentTime = 0;
        }
      }

      audioElement.play();
      setPlayingAudioId(id);
    }
  };

  const handleAudioEnded = () => {
    setPlayingAudioId(null);
  };

  const TagComponent = ({ cat_id }: { cat_id: number }) => {
    const categoryItem = category?.find((cat) => cat.id === cat_id);
    return (
      <>
        {categoryItem?.text ? (
          <div
            className={cn(
              "bg-white border border-borderPrimary !text-textHeadingColor px-2 py-0.5 rounded-full duration_text tracking-wide font-semibold capitalize ",
              normalInter,
            )}
          >
            {categoryItem?.text}
          </div>
        ) : null}
      </>
    );
  };

  const SourceComponent = ({ source_id }: { source_id: number }) => {
    const sourceItem = chopaiSource?.find((src) => src.id === source_id);

    return (
      <div
        className={cn(
          "bg-white border border-borderPrimary !text-textHeadingColor px-2 py-0.5 rounded-full duration_text tracking-wide font-semibold capitalize",
          normalInter,
        )}
      >
        {sourceItem?.text}
      </div>
    );
  };
  return (
    <>
      {data.map((item, i) => {
        const isLastItem = data.indexOf(item) === data.length - 1;
        return (
          <div key={i} className={!isLastItem ? "border-b border-borderPrimary" : ""}>
            <div className="space-y-2 py-4 px-5">
              <p className={cn("!text-textColorPrimary leading-relaxed whitespace-pre-line body_text_m  capitalize", normalInter)}>{item.text}</p>

              <div>
                <div className="flex flex-wrap gap-2 items-center justify-between ">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div
                      className={cn(
                        "bg-primaryColor/15 border border-borderPrimary text-textHeadingColor font-semibold px-2 py-0.5 rounded-full duration_text tracking-wide capitalize",
                        normalInter,
                      )}
                    >
                      {item.type}
                    </div>

                    {item?.category && item?.category?.length > 0
                      ? item?.category?.map((cat_id) => <TagComponent key={cat_id} cat_id={cat_id} />)
                      : null}

                    {item?.source ? <SourceComponent key={item.source} source_id={item.source} /> : null}

                    {item.tag && (
                      <div className="bg-textHeadingColor border border-borderPrimary !text-white px-3 py-1 rounded-full text-12 font-semibold capitalize">
                        {item.tag}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 ">
                    {/* Copy Button */}
                    <ActionButton onClick={() => handleCopy(item.id, item.text)}>
                      {copyText[item.id] ? <CopyCheck className="w-4 h-4 text-textHeadingColor" /> : <Copy className="w-4 h-4" />}
                    </ActionButton>

                    {/* Play/Pause Button */}
                    {item.audio_url && (
                      <ActionButton
                        onClick={() => {
                          const audioElement = document.getElementById(`audio-${item.id}`) as HTMLAudioElement;
                          handlePlayPause(item.id, audioElement);
                        }}
                        isActive={playingAudioId === item.id}
                      >
                        {playingAudioId === item.id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </ActionButton>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Hidden Audio Element */}
            <audio id={`audio-${item.id}`} className="hidden" src={item.audio_url || ""} onEnded={handleAudioEnded}>
              <track kind="captions" src="/captions/audio-captions.vtt" srcLang="en" label="English" />
            </audio>
          </div>
        );
      })}
      <div ref={observerRef} className="h-10"></div>
    </>
  );
});

ListView.displayName = "ListView";

// Helper component for action buttons
const ActionButton = ({
  children,
  onClick,
  isDisabled = false,
  isActive = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  isDisabled?: boolean;
  isActive?: boolean;
}) => {
  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      className={cn(
        "p-2 rounded-full border transition-colors duration-200",
        isActive
          ? "bg-textHeadingColor text-white border-textHeadingColor"
          : "bg-cardBg hover:bg-secondaryColor text-textColorPrimary border-borderPrimary/20",
        isDisabled && "opacity-50 cursor-not-allowed",
      )}
    >
      {children}
    </button>
  );
};

export default ListView;
